import { default as React, useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { GrantRelation } from '../../api/RebacExplorerClient';

// @material-ui/lab ToggleButton is not in older MUI v4 — use plain buttons instead
const TargetTypeToggle = ({
  value,
  onChange,
}: {
  value: 'user' | 'group';
  onChange: (v: 'user' | 'group') => void;
}) => (
  <Box style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
    {(['user', 'group'] as const).map(type => (
      <Button
        key={type}
        size="small"
        variant={value === type ? 'contained' : 'outlined'}
        color={value === type ? 'primary' : 'default'}
        onClick={() => onChange(type)}
        style={{ textTransform: 'capitalize', minWidth: 80 }}
      >
        {type === 'user' ? 'Usuário' : 'Grupo'}
      </Button>
    ))}
  </Box>
);

const RELATION_LABELS: Record<string, string> = {
  viewer:   'Viewer',
  executor: 'Executor',
  editor:   'Editor',
  admin:    'Admin',
};

interface GrantFormProps {
  objectLabel: string;
  availableRelations: GrantRelation[];
  onSubmit: (target: string, relation: GrantRelation, grantDuration?: string) => Promise<void>;
  onCancel: () => void;
  crossTenantError?: string | null;
}

interface CatalogSuggestion {
  name: string;
  displayName: string;
  namespace: string;
}

export function GrantForm({ objectLabel, availableRelations, onSubmit, onCancel, crossTenantError }: GrantFormProps) {
  const catalogApi = useApi(catalogApiRef);
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [targetType, setTargetType] = useState<'user' | 'group'>('user');
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<CatalogSuggestion[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<CatalogSuggestion | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [relation, setRelation] = useState<GrantRelation>(
    () => availableRelations.find(r => r === 'viewer') ?? availableRelations[0],
  );
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQuery('');
    setSelectedTarget(null);
    setSuggestions([]);
    setFieldError(null);
    setExpiresAt(null);
  }, [targetType]);

  useEffect(() => {
    if (crossTenantError) setFieldError(crossTenantError);
  }, [crossTenantError]);

  const search = (q: string) => {
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoadingSuggestions(true);
    const kind = targetType === 'user' ? 'User' : 'Group';
    catalogApi
      .getEntities({ filter: { kind } })
      .then(({ items }) => {
        const lower = q.toLowerCase();
        const results = items
          .filter(e => {
            const nm = e.metadata.name.toLowerCase();
            const dn = ((e.spec as any)?.profile?.displayName ?? '').toLowerCase();
            return nm.includes(lower) || dn.includes(lower);
          })
          .slice(0, 8)
          .map(e => ({
            name: e.metadata.name,
            displayName: (e.spec as any)?.profile?.displayName ?? e.metadata.name,
            namespace: e.metadata.namespace ?? 'default',
          }));
        setSuggestions(results);
        setShowSuggestions(true);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedTarget(null);
    setFieldError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const selectSuggestion = (s: CatalogSuggestion) => {
    setSelectedTarget(s);
    setQuery(s.displayName || s.name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!selectedTarget) {
      setFieldError('Selecione um usuário ou grupo da lista de sugestões.');
      return;
    }
    const prefix = targetType === 'user' ? 'user' : 'group';
    const target = `${prefix}:${selectedTarget.namespace}/${selectedTarget.name}`;
    let grantDuration: string | undefined;
    if (expiresAt) {
      const diffMs = expiresAt.getTime() - Date.now();
      const minutes = Math.ceil(diffMs / (1000 * 60));
      grantDuration = `${Math.max(1, minutes)}m`;
    }
    setSubmitting(true);
    try {
      await onSubmit(target, relation, grantDuration);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
    <Box
      style={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: 20,
        marginBottom: 20,
        backgroundColor: isDark ? theme.palette.background.default : '#fafafa',
      }}
    >
      <Typography variant="subtitle2" style={{ marginBottom: 16, fontWeight: 700 }}>
        Conceder acesso em <strong>{objectLabel}</strong>
      </Typography>

      <TargetTypeToggle value={targetType} onChange={setTargetType} />

      <FormControl fullWidth style={{ marginBottom: 16, position: 'relative' }}>
        <FormLabel style={{ fontSize: 12, marginBottom: 4 }}>
          {targetType === 'user' ? 'Usuário destino' : 'Grupo destino'}
        </FormLabel>
        <TextField
          variant="outlined"
          size="small"
          placeholder={`Buscar ${targetType === 'user' ? 'usuário' : 'grupo'}…`}
          value={query}
          onChange={handleQueryChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          error={Boolean(fieldError)}
          helperText={fieldError ?? undefined}
          InputProps={{
            endAdornment: loadingSuggestions ? <CircularProgress size={16} /> : null,
          }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <Paper
            elevation={4}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            <List dense disablePadding>
              {suggestions.map(s => (
                <ListItem
                  key={`${s.namespace}/${s.name}`}
                  button
                  onMouseDown={() => selectSuggestion(s)}
                >
                  <ListItemText
                    primary={s.displayName || s.name}
                    secondary={`${s.namespace}/${s.name}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </FormControl>

      <FormControl fullWidth style={{ marginBottom: 20 }}>
        <FormLabel style={{ fontSize: 12, marginBottom: 4 }}>Relação</FormLabel>
        <Select
          variant="outlined"
          value={relation}
          onChange={e => setRelation(e.target.value as GrantRelation)}
          inputProps={{ style: { padding: '8px 14px' } }}
        >
          {availableRelations.map(r => (
            <MenuItem key={r} value={r}>
              {RELATION_LABELS[r] ?? r}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth style={{ marginBottom: 20 }}>
        <FormLabel style={{ fontSize: 12, marginBottom: 4 }}>
          Expira em (opcional — vazio = permanente)
        </FormLabel>
        <DateTimePicker
          inputVariant="outlined"
          size="small"
          value={expiresAt}
          onChange={date => setExpiresAt(date as Date | null)}
          disablePast
          format="dd/MM/yyyy HH:mm"
          clearable
          clearLabel="Limpar"
          cancelLabel="Cancelar"
          okLabel="Confirmar"
          placeholder="Selecione data e hora de expiração"
          InputProps={{ style: { fontSize: 14 } }}
        />
        {expiresAt && (
          <Typography variant="caption" style={{ color: theme.palette.warning.main, marginTop: 4 }}>
            Acesso temporário — expirará em {expiresAt.toLocaleString('pt-BR')}.
          </Typography>
        )}
      </FormControl>

      <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSubmit}
          disabled={submitting || !selectedTarget}
        >
          {submitting ? <CircularProgress size={16} /> : 'Confirmar Concessão'}
        </Button>
      </Box>
    </Box>
    </MuiPickersUtilsProvider>
  );
}
